type AnyObject = Record<string, any>

const createStorageFallback = () => {
  const data = new Map<string, string>()

  return {
    clear: () => data.clear(),

    getItem: (key: string) => data.get(key) ?? null,

    key: (index: number) => {
      const keys = Array.from(data.keys())

      return (keys[index] as string | undefined) ?? null
    },

    get length() {
      return data.size
    },

    removeItem: (key: string) => {
      data.delete(key)
    },

    setItem: (key: string, value: string) => {
      data.set(key, value)
    }
  }
}

const getItemIn = <T = any>(name: string, storage: Storage): T =>
  JSON.parse(storage.getItem(name) ?? '{}')

const setItemIn = (name: string, value: AnyObject, storage: Storage) =>
  storage.setItem(name, JSON.stringify(value))

export const accessStorage = <T extends AnyObject>(
  name: string,
  defaults: T,
  receivedStorage?: Storage
) => {
  const storage =
    receivedStorage ??
    (typeof window === 'undefined' ? createStorageFallback() : localStorage)

  const get = () => getItemIn<T>(name, storage)
  const set = (value: T) => setItemIn(name, value, storage)
  const remove = () => storage.removeItem(name)

  const getItem = <K extends keyof T>(key: K) => get()[key]

  const setItem = <K extends keyof T>(key: K, value: T[K]) => {
    const data = get()
    data[key] = value

    set(data)
  }

  const init = () => set({ ...defaults, ...get() })

  init()

  return { get, set, remove, getItem, setItem }
}
